import { Trans } from '@lingui/react/macro'
import {
  Button,
  Group,
  LoadingOverlay,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import { AvailableTimeSlot } from '@markku/types'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import dayjs from 'dayjs'

interface DateTimeStepProps {
  selectedDate: Date
  selectedTime?: AvailableTimeSlot
  onDateChange: (date: Date) => void
  onTimeSelect: (slot: AvailableTimeSlot) => void
  timeSlots: AvailableTimeSlot[]
  isLoading: boolean
  onNext: () => void
  onBack: () => void
  isSubmitting: boolean
}

function getNextWeekday(date: dayjs.Dayjs, direction: 'prev' | 'next'): Date {
  let current = date
  const step = direction === 'prev' ? -1 : 1

  current = current.add(step, 'day')

  while (current.day() === 0 || current.day() === 6) {
    current = current.add(step, 'day')
  }

  return current.toDate()
}

export function DateTimeStep({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeSelect,
  timeSlots,
  isLoading,
  onNext,
  onBack,
  isSubmitting,
}: DateTimeStepProps) {
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = getNextWeekday(dayjs(selectedDate), direction)
    onDateChange(newDate)
  }

  const formattedDate = dayjs(selectedDate).format('dddd, D.MM')
  const canGoPrev = !dayjs(selectedDate).isBefore(dayjs(), 'day')

  return (
    <Stack gap="md" pos="relative">
      <div>
        <Text size="xl" fw={600} mb="xs">
          <Trans>Select date and time</Trans>
        </Text>
        <Text size="sm" c="dimmed">
          <Trans>Choose a suitable time for the survey visit</Trans>
        </Text>
      </div>

      <Group justify="center" gap="md" wrap="nowrap">
        <Button
          variant="light"
          onClick={() => navigateDate('prev')}
          size="lg"
          disabled={!canGoPrev}
        >
          <IconChevronLeft size={24} />
        </Button>

        <Text size="lg" fw={500} ta="center" style={{ minWidth: 150 }}>
          {formattedDate}
        </Text>

        <Button variant="light" onClick={() => navigateDate('next')} size="lg">
          <IconChevronRight size={24} />
        </Button>
      </Group>

      <div style={{ position: 'relative', minHeight: 200 }}>
        <LoadingOverlay visible={isLoading} />

        {!isLoading && timeSlots.length === 0 ? (
          <Text ta="center" py="xl">
            <Trans>
              No available times for the selected day, check the next day.
            </Trans>
          </Text>
        ) : (
          <SimpleGrid cols={3} spacing="sm">
            {timeSlots.map((slot) => {
              const slotTime = new Date(slot.startTime)
              const isSelected = selectedTime?.startTime === slot.startTime

              return (
                <Button
                  key={slot.startTime}
                  variant={isSelected ? 'filled' : 'default'}
                  onClick={() => onTimeSelect(slot)}
                  size="md"
                >
                  {dayjs(slotTime).format('HH:mm')}
                </Button>
              )
            })}
          </SimpleGrid>
        )}
      </div>

      <Group mt="md">
        <Button
          variant="default"
          onClick={onBack}
          size="md"
          flex={1}
          leftSection={<IconChevronLeft size={18} />}
          disabled={isSubmitting}
        >
          <Trans>Back</Trans>
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedTime}
          size="md"
          flex={1}
          rightSection={<IconChevronRight size={18} />}
          loading={isSubmitting}
        >
          <Trans>Continue</Trans>
        </Button>
      </Group>
    </Stack>
  )
}
